import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  createStyles,
  Fade,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { darken, fade, lighten } from '@material-ui/core/styles/colorManipulator';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import React, { useEffect, useRef, useState } from 'react';
import CopyResultsWidget from '../../CopyResultsWidget/CopyResultsWidget';
import { TestResults, TestWarnings } from '../../types';
import { getConnectivityAlertVariant } from '../../utils';
import { rows } from '../rows';
import { CheckConnecitivityResults } from './CheckConnectivityResults/CheckConnectivityResults';
import './ConnectivityTestResults.css';

const useStyles = makeStyles((theme: Theme) => {
  const getBackgroundColor = theme.palette.type === 'light' ? lighten : darken;
  return createStyles({
    table: {
      tableLayout: 'fixed',
      borderTop: `1px solid ${theme.palette.divider}`,
      '& td, & th': {
        width: '260px',
        borderRight: `1px solid
      ${
        // Same implementation as material-ui's table borderBottom.
        theme.palette.type === 'light'
          ? lighten(fade(theme.palette.divider, 1), 0.88)
          : darken(fade(theme.palette.divider, 1), 0.68)
      }`,
      },
      '& td:last-child, & th:last-child': {
        borderRight: 'none',
      },
      '& th, & td:first-child': {
        fontWeight: 'bold',
      },
    },
    tableBorder: {
      border: '1px solid rgba(0, 0, 0, 0.12)',
    },
    tableCellContent: {
      display: 'flex',
      alignItems: 'center',
      wordBreak: 'break-word',
      '& svg': {
        fill: '#000',
      },
    },
    headerContent: {
      display: 'flex',
      '& p': {
        fontWeight: 'bold',
        marginLeft: '12px',
      },
    },
    bestEdgeCell: {
      background: getBackgroundColor(theme.palette.success.main, 0.9),
    },
    tableHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1em',
    },
    [TestWarnings.warn]: {
      background: getBackgroundColor(theme.palette.warning.main, 0.9),
    },
    [TestWarnings.error]: {
      background: getBackgroundColor(theme.palette.error.main, 0.9),
    },
  });
});

const ConnectivityTestResults = ({ results }: { results?: TestResults }) => {
  const classes = useStyles();

  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const accordionSummaryRef = useRef<any>(null);

  useEffect(() => {
    if (results && getConnectivityAlertVariant(results) === 'error') {
      setIsAccordionOpen(true);
    }
  }, [results]);

  if (!results) return null;

  const getTableCellClass = (warning?: TestWarnings) => {
    if (warning?.includes('warn')) return classes[TestWarnings.warn];
    if (warning === TestWarnings.error) return classes[TestWarnings.error];
  };

  const getTableRowWithResults = (row: any) => {
    const value = row.getValue(results);
    const warning = row.getWarning?.(results);
    const className = getTableCellClass(warning);
    const tooltipContent = warning ? row.tooltipContent?.[warning] : null;

    return (
      <TableCell key={results?.edge} className={className}>
        <div className={classes.tableCellContent}>
          {value}
          {tooltipContent && (
            <Tooltip title={tooltipContent} placement="top" interactive leaveDelay={250}>
              <InfoIcon className="tool-tip-icon" />
            </Tooltip>
          )}
        </div>
      </TableCell>
    );
  };

  const scrollAccordionIntoView = () => {
    if (isAccordionOpen && accordionSummaryRef.current) {
      setTimeout(() => {
        accordionSummaryRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const toggleAccordion = () => setIsAccordionOpen((isOpen: boolean) => !isOpen);

  return (
    <Fade in={true} timeout={1000}>
      <Paper elevation={3} className="connectivity-tests-results-container" ref={accordionSummaryRef}>
        <div className={classes.tableHeader}>
          <Typography variant="h5" paragraph>
            Connectivity and Bandwidth Tests
          </Typography>
          <CopyResultsWidget results={results} />
        </div>
        <CheckConnecitivityResults results={results} />

        <Box padding={2}>
          <Accordion
            expanded={isAccordionOpen}
            TransitionProps={{
              addEndListener: scrollAccordionIntoView,
            }}
          >
            <AccordionSummary
              onClick={toggleAccordion}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography>Show Detailed Results</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer className={classes.tableBorder}>
                <Table className={classes.table}>
                  <TableBody>
                    {rows.map((row) => {
                      const tooltipTitle = row.tooltipContent?.label;
                      return (
                        <TableRow key={row.label}>
                          <TableCell>
                            <div className={classes.tableCellContent}>
                              {row.label}
                              {tooltipTitle && (
                                <Tooltip title={tooltipTitle} placement="top" interactive leaveDelay={250}>
                                  <InfoIcon className="tool-tip-icon" />
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>
                          {getTableRowWithResults(row)}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Paper>
    </Fade>
  );
};

export { ConnectivityTestResults };
